import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Top-level strings
      dashboardMenu: 'Dashboard',        // renamed to avoid conflict
      login: 'Login',
      calendar: 'Calendar',
      reports: 'Reports',
      inpatient_charts: 'Admissions',
      manage_staff: 'Manage Staff',
      settings: 'Settings',
      hello_doctor: 'Hello Doctor',
      offline_patients: 'Offline Patients',
      online_patients: 'Online Patients',
      in_patients: 'In Patients',
      add_article: 'Add Article',
      add_schedule: 'Add Schedule',
      hello: 'Hello',
      logout: 'Logout',
  "patient_checkout": "Patient Checkout",

      // Header section
      header: {
        logoAlt: 'Laafi Logo',
        viewDoctor: 'View Doctor',
        bookConsultation: 'Book Consultation',
        loginSignup: 'Login / Signup',
        login: 'Login',
        signup: 'Sign Up',
        toggleLanguage: 'Change Language',
        english: 'English',
        french: 'French',
      },
 "selectDept": {
    "next": "Next",
    "selectSlotFirst": "Please select a date and time first",
    "selectPaymentMode": "Select Payment Mode",
    "integrationPending": "Integration Pending",
    "payAndConfirm": "Confirm & Submit",
    "cardIntegrationPending": "Card payment integration is pending. Please select Cash.",
    "selectPaymentMode": "Please select a payment mode",
    "consultationAddedSuccess": "Consultation booked successfully!",
    "consultationFailed": "Failed to book consultation. Please try again.",
    "noPaymentModes": "No payment modes available",
    "errorFetchingPaymentTypes": "Failed to load payment modes. Please try again.",
    "selectAllFields": "Please select a doctor, day, and time slot.",
    "consultationAddedSuccess": "Consultation Added successfully!",
    "initiatingPayment": "Consultation Added. Initiating payment...",
    "consultationFailed": "Failed to add consultation",
    "bookConsultation": "Book a consultation",
    "onlineConsultation": "Online Consultation",
    "hospitalAppointment": "Hospital Appointment",
    "departmentSelect": "Department select",
    "selectDepartment": "Select Department",
    "myself": "myself",
    "doctorImage": "Doctor Image",
    "date": "Date",
    "time": "Time",
    "totalPayable": "Total Payable",
    "paymentSummary": "Payment Summary",
    "consultationFee": "Consultation Fee",
    "payConfirm": "Pay & Confirm",
    "chooseDoctor": "Who would you like to consult?",
    "chooseTime": "When would you like to consult?",
    "logoAlt": "Laafi Logo",
    "noDoctorsAvailable": "No doctors available for this department.",
    "noSlotsAvailable": "No available time slots for this date.",
    "errorFetchingDoctors": "Failed to load doctors. Please try again.",
    "slotsFetchFailed": "Failed to load available slots. Please try again."
  },
      // About Laafi section
      aboutLaafi: {
        title: 'About Laafi',
          eyebrow: 'Smart care operations',
  heading: 'Professional healthcare coordination for every patient journey.',
        description1: 'Laafi is an all-in-one hospital management system designed to streamline and automate hospital activities – from patient registration to discharge.',
        description2: 'With an ',
        aiPowered: 'AI‑assisted diagnostic process',
        description3: ', Laafi enhances patient care through intelligent, data‑driven workflows, enabling healthcare professionals to ',
        focusOnLives: 'focus on what matters most: saving lives',
        description4: '.',
        keyFeatures: 'Key Features',
        aiDiagnosis: 'AI‑Assisted Diagnosis',
        aiDiagnosisDesc: 'Intelligent diagnostic support for healthcare professionals',
        workflowAutomation: 'Workflow Automation',
        workflowAutomationDesc: 'Automated processes from admission to discharge',
        patientCare: 'Enhanced Patient Care',
        patientCareDesc: 'Focus on better health outcomes',
      },

      // What We Offer section
      whatweoffer: {
        title: 'What We Offer',
        secureAuthentication: 'Secure Authentication',
        bloodBank: 'Blood Bank',
        onlineConsultation: 'Online Consultation',
        patientManagement: 'Patient Management',
        labs: 'Labs',
        scheduling: 'Scheduling',
        humanResources: 'Human Resources',
        aiDiagnosis: 'AI Diagnosis',
        pharmacy: 'Pharmacy',
        remoteMonitoring: 'Remote Monitoring',
        houseKeeping: 'Housekeeping',
        descriptions: {
          secureAuthentication: 'Advanced security protocols to protect patient data',
          bloodBank: 'Comprehensive blood bank management system',
          onlineConsultation: 'Virtual consultations with healthcare professionals',
          patientManagement: 'Complete patient care management',
          labs: 'Integrated laboratory management system',
          scheduling: 'Intelligent appointment and resource scheduling',
          humanResources: 'Staff management and HR operations',
          aiDiagnosis: 'AI‑powered diagnostic assistance',
          pharmacy: 'Complete pharmacy management solution',
          remoteMonitoring: 'Remote patient monitoring capabilities',
          houseKeeping: 'Hospital housekeeping and maintenance',
        },
      },

      // Download Links section
      downloadLinks: {
        title: 'Download the Laafi App',
        description: 'Access video consultations with doctors on the Laafi app. Connect with online doctors, available 24/7, from the comfort of your home.',
        subtitle: 'Get the link to download the app',
        phonePlaceholder: 'Enter phone number',
        sendSMS: 'Send SMS',
        smsSentMessage: 'Download link sent to',
        googlePlayAlt: 'Get it on Google Play',
        googlePlayLabel: 'Download Laafi on Google Play',
        appStoreAlt: 'Download on the App Store',
        appStoreLabel: 'Download Laafi on the App Store',
        imageAlt: 'Mobile app medical consultation',
        scanQR: 'Or scan the QR code',
        scanForAndroid: 'Scan for Android',
        scanForIOS: 'Scan for iOS',
        features: {
          videoConsultation: 'Video Consultation',
          videoDescription: 'Connect with doctors via secure video calls',
          appointments: 'Book Appointments',
          appointmentsDescription: 'Schedule in‑person or online appointments',
          records: 'Medical Records',
          recordsDescription: 'Access your health records anytime',
          prescriptions: 'Digital Prescriptions',
          prescriptionsDescription: 'Receive prescriptions on your phone',
        },
      },

      // Auth section
      auth: {
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        forgotPassword: 'Forgot Password?',
        rememberMe: 'Remember Me',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        signupHere: 'Sign up here',
        loginHere: 'Login here',
      },

      // Doctor section
      doctor: {
        search: 'Search Doctors',
        specialty: 'Specialty',
        availability: 'Availability',
        schedule: 'Schedule Appointment',
        qualification: 'Qualification',
        experience: 'Years of Experience',
        rating: 'Rating',
        bookNow: 'Book Now',
        viewProfile: 'View Profile',
        allSpecialties: 'All Specialties',
      },

      // Appointment section
      appointment: {
        title: 'Book Consultation',
        date: 'Date',
        time: 'Time',
        reason: 'Reason for Visit',
        symptoms: 'Symptoms',
        book: 'Book Appointment',
        cancel: 'Cancel',
        type: 'Appointment Type',
        inPerson: 'In Person',
        telemedicine: 'Telemedicine',
        emergency: 'Emergency',
      },

      // Dashboard section (nested object – used for dashboard-specific translations)
      dashboard: {
        welcome: 'Welcome',
        upcoming: 'Upcoming Appointments',
        past: 'Past Appointments',
        medicalRecords: 'Medical Records',
        prescriptions: 'Prescriptions',
        labResults: 'Lab Results',
        billing: 'Billing',
        settings: 'Settings',
        profile: 'Profile',
        logout: 'Logout',
      },

      // Patient related
      patient: {
        consultationDetails: 'Consultation Details',
        mySchedules: 'My Schedules',
        bookAppointment: 'Book Appointment',
        bookingInfo: 'Booking Information',
        bookingDescription: "Click on 'Book Appointment' to schedule a new consultation.",
        pastConsultations: 'Past Consultations',
        upcomingSchedules: 'Upcoming Schedules',
      },

      // Table headers
      table: {
        date: 'DATE',
        doctor: 'DOCTOR',
        patientName: 'PATIENT NAME',
        report: 'REPORT',
        status: 'STATUS',
        action: 'ACTION',
      },

      // Buttons
      button: {
        viewReport: 'View Report',
        edit: 'Edit',
        delete: 'Delete',
        save: 'Save',
        cancel: 'Cancel',
        confirm: 'Confirm',
        close: 'Close',
        next: 'Next',
        back: 'Back',
        submit: 'Submit',
        reset: 'Reset',
      },

      // Messages
      message: {
        noPastConsultation: 'No past consultations found',
        loading: 'Loading...',
        noData: 'No data available',
        success: 'Operation completed successfully',
        error: 'An error occurred',
        confirmDelete: 'Are you sure you want to delete this item?',
        saveSuccess: 'Saved successfully',
        updateSuccess: 'Updated successfully',
      },

      // Common terms (COMPREHENSIVE - NO DUPLICATES)
      common: {
        welcome: 'Welcome',
        hello: 'Hello',
        dashboard: 'Dashboard',
        profile: 'Profile',
        settings: 'Settings',
        logout: 'Logout',
        login: 'Login',
        register: 'Register',
        search: 'Search',
        filter: 'Filter',
        all: 'All',
        pending: 'Pending',
        completed: 'Completed',
        cancelled: 'Cancelled',
        active: 'Active',
        inactive: 'Inactive',
        notAvailable: 'N/A',
        save: 'Save',
        cancel: 'Cancel',
        update: 'Update',
        delete: 'Delete',
        addNew: 'Add New',
        print: 'Print',
        download: 'Download',
        upload: 'Upload',
        yes: 'Yes',
        no: 'No',
        warning: 'Warning',
        info: 'Information',
        manage_patients: 'Manage Patients',
      },

     medical: {
  consultation: 'Consultation',
  prescription: 'Prescription',
  diagnosis: 'Diagnosis',
  treatment: 'Treatment',
  medication: 'Medication',
  allergy: 'Allergy',
  symptoms: 'Symptoms',
  history: 'Medical History',
  vitalSigns: 'Vital Signs',
  labResults: 'Lab Results',
  radiology: 'Radiology',
  pharmacy: 'Pharmacy',
  patientDetails: 'Patient Details',
  labTests: 'Lab Tests',
  surgerySchedule: 'Surgery Schedule',
  reviewComplete: 'Review+Complete',
  completeConsultation: 'Complete Consultation',
  allergies: 'Allergies',
  medications: 'Medications',
  temperature: 'Temperature',
  bloodPressure: 'Blood Pressure',
  heartRate: 'Heart Rate',
  heightWeight: 'Height & Weight',
  bmi: 'BMI',
  immunizations: 'Immunizations',
  // ADD THESE MISSING KEYS:
  male: 'Male',
  female: 'Female',
  gender: 'Gender',
  dateOfBirth: 'Date of Birth',
},

      // Time/Date
      time: {
        today: 'Today',
        yesterday: 'Yesterday',
        tomorrow: 'Tomorrow',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        nextMonth: 'Next Month',
        lastMonth: 'Last Month',
        date: 'Date',
        time: 'Time',
        duration: 'Duration',
      },

      // Navigation (COMPREHENSIVE - NO DUPLICATES)
      navigation: {
        home: 'Home',
        appointments: 'Appointments',
        reports: 'Reports',
        medications: 'Medications',
        billing: 'Billing',
        help: 'Help',
        support: 'Support',
        about: 'About Us',
        services: 'Services',
        contact: 'Contact Us',
        faq: 'FAQ',
        privacy: 'Privacy Policy',
        terms: 'Terms & Conditions',
      },

      // Form labels
      form: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        phone: 'Phone',
        address: 'Address',
        city: 'City',
        state: 'State',
        zipCode: 'Zip Code',
        country: 'Country',
        gender: 'Gender',
        birthDate: 'Date of Birth',
        age: 'Age',
        bloodType: 'Blood Type',
        emergencyContact: 'Emergency Contact',
      },

      // Image alt texts
      alt: {
        laafiLogo: 'Laafi Logo',
        userAvatar: 'User Avatar',
        doctorIcon: 'Doctor Icon',
        patientIcon: 'Patient Icon',
        appointmentIcon: 'Appointment Icon',
      },

      // Login component
      login: {
        signup: 'Login/Signup',
        loginSignup: 'Login/Signup',
        patient: 'Patient',
        doctor: 'Doctor',
        staff: 'Staff',
        phoneEmail: 'Phone / Email',
        or: 'OR',
        googleLogin: 'Login with Google',
        terms: 'Terms of use',
        privacy: 'Privacy policy',
        welcomeBack: 'Welcome Back {{name}}',
        enterMPIN: 'Please enter your MPIN',
        forgotMPIN: 'Forgot MPIN',
        loginWithOTP: 'Login with OTP',
        notYou: 'Not You?',
      },

      // Basic details (appointment booking)
      basicDetails: {
        consultOnline: 'Consult a Doctor Online',
        bookHospitalAppointment: 'Book a Hospital Appointment',
        bookConsultation: 'Book a consultation',
        selectDepartment: 'Select a department',
        selectDobError: 'Please select your Date of Birth.',
      },

      // Doctor dashboard
     doctorDashboard: {
  myConsultations: 'My pending consultations',
  myPatients: 'My Patients',
  mySchedules: 'My Schedules',
  patientServices: 'Patient Statistics', // Add this line
  searchPlaceholder: 'Search For Patient Records',
  logoAlt: 'Laafi Logo',
  
},


      // Patient details
      patientDetails: {
        name: 'Name',
        age: 'Age',
        gender: 'Gender',
        mrn: 'MRN',
        notAvailable: 'N/A',
        noPatientSelected: 'No patient selected',
      },

      // Consultation
      consultation: {
        consultationDetails: 'Consultation Details',
        hpiPlaceholder: 'HPI...',
        chiefComplaintPlaceholder: 'Complaints...',
        systemicExaminationPlaceholder: 'Physical Examinations...',
        finalDiagnosis: 'Final Diagnosis',
        addDiagnosis: 'Add Diagnosis',
        saveDiagnoses: 'Save Diagnoses',
        primaryDiagnosis: 'Primary Diagnosis',
        primaryDiagnosisPlaceholder: 'Enter primary diagnosis...',
        notes: 'Notes',
        primaryDiagnosisNotesPlaceholder: 'Notes for primary diagnosis...',
        additionalDiagnosis: 'Additional Diagnosis',
        diagnosis: 'Diagnosis',
        additionalDiagnosisPlaceholder: 'Enter additional diagnosis...',
        additionalDiagnosisNotesPlaceholder: 'Notes for additional diagnosis...',
        useSpecialtyTemplate: 'Use Specialty Template',
        aiDiagnosis: 'AI Diagnosis',
        aiAnalysis: 'AI Analysis',
        patientHistory: 'Patient History',
        viewPastConsultations: 'Past Medical History',
        patientImages: 'Patient Images',
        uploadedImageAlt: 'Uploaded Image',
        diagnosisRegistered: 'Diagnosis registered successfully!',
        diagnosisRegisterFailed: 'Failed to register diagnosis. Please try again.',
        diagnosesSaved: 'Diagnoses saved successfully!',
        diagnosesSaveFailed: 'Failed to save diagnoses. Please try again.',
        addDiagnosisWarning: 'Please add at least one diagnosis before saving.',
        addToFinalDiagnosis: 'Add to Final Diagnosis',
        aiRecommendation: 'AI Recommendation',
        noAiRecommendation: 'No specific recommendation text available from AI.',
        aiDiagnosisFailed: 'AI Diagnosis failed. Please try again.',
        aiAnalysisFailed: 'AI analysis failed. Please try again.',
        addNewDiagnosis: 'Add new diagnosis: "{{diagnosis}}"',
        diagnosisNotInSystem: 'This diagnosis is not in the system',
        addNew: 'Add New',
      },
 "medicineDetails": {
  "prescribeMedicines": "Prescribe Medicines",
  "noMedicinesPrescribed": "No medicines prescribed",
  "prescribeMedicineButton": "Prescribe Medicine",
  "printPrescription": "Print Prescription",
  "searchMedicines": "Search prescribed medicines...",
  "noMatchingMedicines": "No matching medicines found",
  "medicationInfo": "Medication Info",
  "posology": "Posology",
  "dose": "Dose",
  "frequency": "Frequency",
  "treatmentDuration": "Treatment Duration",
  "days": "days",
  "refills": "refill(s)",
  "beforeMeal": "Before meal",
  "afterMeal": "After meal",
  "laafiRecommendation": "Laafi Recommendations",
  "laafiDescription": "Based on the diagnosis, the following medicines are recommended:",
  "noRecommendations": "No recommendations available",
  "disclaimer": "Please review these recommendations before prescribing"
},
  "labTest": {
    "prescribeTests": "Prescribe Tests",
    "healthPackages": "Health Packages:",
    "individualTests": "Individual Tests:",
    "noTestsPrescribed": "No Tests prescribed",
    "prescribeHealthPackageButton": "Prescribe health package",
    "prescribeTestButton": "Prescribe test",
    "printBulletin": "Print",
    "prescribeHealthPackage": "Prescribe Health Package",
    "prescribeLabTest": "Prescribe Lab Test",
    "noHealthPackages": "No health packages available.",
    "noLabTests": "No lab tests available.",
    "addPackages": "Add Packages",
    "addTests": "Add Tests",
    "unknownPackage": "Unknown Package",
    "unknownTest": "Unknown Test"
  },
  // Add to prescribeDialog section
"prescribeDialog": {
  "title": "Prescribe Medicine",
  "editTitle": "Edit Medicine",
  "medicineInformation": "Medicine Information",
  "medicineNameLabel": "Medicine Name",
  "medicineNameHelperTextExisting": "Select an existing medicine",
  "medicineNameHelperTextNew": "New medicine - click Add to list",
  "newMedicine": "New Medicine",
  "newMedicineOption": "Add \"{{medicineName}}\"",
  "newMedicineDescription": "This medicine is not in the list",
  "adding": "Adding...",
  "addToMasterButton": "Add to list",
  "genericLabel": "Generic: {{genericName}}",
  "dosageAndForm": "Dosage & Form",
  "dosage": "Dosage",
  "dosagePlaceholder": "e.g., 500mg, 10mg/5ml, 100mg/ml",
  "dosageHelperText": "Enter the strength/dosage of the medication",
  "prescriptionForm": "Prescription Form",
  "selectForm": "Select form",
  "form": {
    "tabletScored": "Scored Tablet",
    "tabletPlain": "Plain Tablet",
    "syrup": "Syrup",
    "injectable": "Injectable",
    "capsule": "Capsule",
    "inhaler": "Inhaler",
    "drops": "Drops",
    "suppository": "Suppository",
    "sachet": "Sachet",
    "ointment": "Ointment/Cream"
  },
 "quantityPerDoseSection": "Quantity per Dose",
"quantityPerDose": "Quantity per dose",
"quantityPerDosePlaceholder": "e.g., 1 tablet, 5 ml, 1 tablespoon, 2 capsules",
"quantityPerDoseHelper": "Specify the exact amount per dose (tablets, ml, mg, etc.)",
"alertNoQuantityPerDose": "Please enter the quantity per dose",
"treatmentDuration": "Treatment Duration",
  "whenToTake": "Posology",
  "beforeFood": "Before meal",
  "onceDaily": "1 mor",
  "twiceDaily": "1 mor, 1 aft",
  "thriceDaily": "1 mor, 1 aft, 1 eve",
  "morning": "Morning",
  "afternoon": "Afternoon",
  "evening": "Evening",
  "night": "Night",
  "custom": "Custom",
  "numberOfPills": "Number of pills",
  "prescriptionPeriod": "Treatment Duration",
  "startDate": "Start Date",
  "endDate": "End Date",
  "or": "OR",
  "enterFixedPills": "Number of days",
  "fixedPillsHelperText": "Total number of treatment days",
  "calculatedTotalPills": "Total: {{fixedPills}} pills for {{days}} days",
  "refills": "Refills",
  "allowRefills": "Allow refills",
  "numberOfRefills": "Number of refills",
  "totalPillsIncludingRefills": "Total with refills: {{total}} pills",
  "saving": "Saving...",
  "savePrescription": "Save Prescription",
  "updatePrescription": "Update",
  "vidalLink": "🔍 Medication Info (Vidal)",
  "vidalLinkWithMedicine": "🔍 Check {{medicine}} on Vidal",
  "alertNoMedicineName": "Please enter a medicine name",
  "alertAddToMasterFirst": "Please add this medicine to the list before continuing",
  "alertNoDosage": "Please enter the dosage",
  "alertNoForm": "Please select the medication form",
  "alertNoPillCounts": "Please specify the number of pills for at least one time of day",
  "alertNoPrescriptionPeriod": "Please specify the prescription period (dates or number of days)",
  "alertMedicineIdNotFound": "Error: Medicine not found. Please select an existing medicine",
  "alertFailedToSave": "Error saving prescription. Please try again",
  "medicineAddedAlert": "{{medicineName}} has been successfully added to the medicine list",
  "medicineAddFailedAlert": "Error adding medicine. Please try again",
  "medicineSavedSuccess": "Medicine saved successfully!",
  "medicineUpdatedSuccess": "Medicine updated successfully!",
  "endDateCannotBeBeforeStartDate": "End date cannot be before start date",
  "selectStartDateFirst": "Please select a start date first",
  "medicineNameRequired": "Medicine name is required"
},
      // Review
      review: {
        reviewConsultation: 'Review Consultation',
        patientDetails: 'Patient Details',
        name: 'Name',
        age: 'Age',
        gender: 'Gender',
        consultationDetails: 'Consultation Details',
        chiefComplaint: 'Chief Complaint',
        finalDiagnosis: 'Final Diagnosis',
        hpi: 'HPI',
        prescribedMedicines: 'Prescribed Medicines',
        noMedicinesPrescribed: 'No medicines prescribed',
      },

      // // Prescribe dialog
      // prescribeDialog: {
      //   medicineInformation: 'Medicine Information',
      //   medicineNameLabel: 'Medicine Name',
      //   medicineNameHelperTextNew: "Type and click 'Add to Master' in the list to register it.",
      //   medicineNameHelperTextExisting: 'Select from existing medicines or type a new one',
      //   whenToTake: 'When to take',
      //   beforeFood: 'Before Food',
      //   onceDaily: 'Once Daily (OID)',
      //   twiceDaily: 'Twice Daily (BID)',
      //   thriceDaily: 'Thrice Daily (TID)',
      //   custom: 'Custom',
      //   morning: 'Morning',
      //   afternoon: 'Afternoon',
      //   evening: 'Evening',
      //   night: 'Night',
      //   numberOfPills: 'Number of Pills',
      //   prescriptionPeriod: 'Prescription Period',
      //   startDate: 'Start Date',
      //   endDate: 'End Date',
      //   or: '― OR ―',
      //   enterFixedPills: 'Enter fixed number of pills',
      //   fixedPillsHelperText: "Enter total number of pills if you don't want to specify dates",
      //   calculatedTotalPills: 'Calculated total pills: {{fixedPills}} ({{days}} days)',
      //   refills: 'Refills',
      //   allowRefills: 'Allow Refills?',
      //   numberOfRefills: 'Number of Refills',
      //   totalPillsIncludingRefills: 'Total pills including refills: {{total}}',
      //   cancel: 'Cancel',
      //   savePrescription: 'Save Prescription',
      //   addToMasterButton: 'Add to Master',
      //   adding: 'Adding...',
      //   newMedicineOption: 'Add "{{medicineName}}"',
      //   newMedicineDescription: 'New Medicine - Click **Add to Master**',
      //   newMedicine: 'New medicine',
      //   genericLabel: 'Generic: {{genericName}}',
      //   medicineAddedAlert: 'Medicine "{{medicineName}}" added to master list.',
      //   medicineAddFailedAlert: 'Failed to add new medicine. Please try again.',
      //   alertNoMedicineName: 'Please enter Medicine Name.',
      //   alertAddToMasterFirst: "Please add the new medicine to the master list first using the 'Add to Master' button, or select an existing one.",
      //   alertNoPillCounts: 'Please specify when to take the medicine by setting pill counts.',
      //   alertNoPrescriptionPeriod: 'Please specify either Prescription Period (Start and End Date) or Fixed Number of Pills.',
      //   alertMedicineIdNotFound: 'Could not find the medicine ID. Please close and try again or ensure the medicine is correctly added to master.',
      //   alertFailedToSave: 'Failed to save prescription. Please try again.',
      //   saving: 'Saving...',
      // },

      // Template selection
      templateSelection: {
        selectTemplate: 'Select a Template',
        department: 'Department:',
        tempId: 'Temp ID:',
        name: 'Name:',
        image: 'Image',
      },

      // Surgery
      surgery: {
        surgeryScheduling: 'Surgery Scheduling',
        surgeryEnabled: 'Surgery Scheduling Enabled',
        addSurgerySchedule: 'Add Surgery Schedule for this Patient?',
        patientInformation: 'Patient Information',
        mrn: 'MRN',
        name: 'Name',
        notAvailable: 'N/A',
        surgicalProcedure: 'Surgical Procedure',
        selectProcedure: 'Select a procedure',
        primarySurgeon: 'Primary Surgeon',
        selectSurgeon: 'Select a surgeon',
        surgeonName: 'Surgeon Name',
        dr: 'Dr.',
        operatingRoom: 'Operating Room',
        selectOperatingRoom: 'Select an operating room',
        roomType: 'Room Type',
        category: 'Category',
        scheduledDateTime: 'Scheduled Date & Time',
        durationMinutes: 'Duration (minutes)',
        anesthesiaDosage: 'Anesthesia Dosage (mg)',
        status: 'Status',
        notStarted: 'Not Started',
        scheduled: 'Scheduled',
        inProgress: 'In Progress',
        completed: 'Completed',
        cancelled: 'Cancelled',
        notes: 'Notes',
        notesPlaceholder: 'Enter any additional notes or special instructions',
        scheduleSurgery: 'Schedule Surgery',
        scheduling: 'Scheduling...',
        loadingMasterData: 'Loading master data...',
        toggleInstructions: 'Toggle the switch above to schedule a surgery for this patient.',
        requiredFields: 'Please fill all required fields: Surgical Procedure, Doctor, Room, Scheduled Date, and Duration',
        scheduledSuccess: 'Surgery scheduled successfully!',
        scheduleFailed: 'Failed to schedule surgery. Please try again.',
        error: 'Error',
        roomTypes: {
          ot: 'OT',
          generalWard: 'GeneralWard',
          nicu: 'NICU',
          icu: 'ICU',
          specialWard: 'SpecialWard',
          emergencyCare: 'EmergencyCare',
          labour: 'Labour',
          store: 'Store',
          sterileUnit: 'SterileUnit',
          surgicalWard: 'SurgicalWard',
          staff: 'Staff',
          nightDuty: 'NightDuty',
        },
        roomCategories: {
          basic: 'Basic',
          special: 'Special',
          delux: 'Delux',
          superDelux: 'SuperDelux',
          luxury: 'Luxury',
          vip: 'VIP',
          vvip: 'VVIP',
        },
      },

      // Past consultations
      pastConsultations: {
        consultationHistory: 'Consultation History',
        patientName: 'Patient Name',
        doctorName: 'Doctor Name',
        department: 'Department',
        date: 'Date',
        consultationType: 'Consultation Type',
        status: 'Status',
        meetingLink: 'Meeting Link',
        join: 'Join',
      },

      // Payment
      payment: {
        failed: 'Payment failed!',
        success: 'Payment successful!',
      },

      "selectDept": {
    "next": "Next",
    "processing": "Processing...",
    "processingPayment": "Processing your payment...",
    "consultationAddedSuccess": "Consultation booked successfully!",
    "selectSlotFirst": "Please select a date and time first",
    "selectPaymentMode": "Select Payment Mode",
    "integrationPending": "Integration Pending",
    "payAndConfirm": "Confirm & Submit",
    "cardIntegrationPending": "Card payment integration is pending. Please select Cash.",
    "selectPaymentMode": "Please select a payment mode",
    "consultationAddedSuccess": "Consultation booked successfully!",
    "consultationFailed": "Failed to book consultation. Please try again.",
    "noPaymentModes": "No payment modes available",
    "errorFetchingPaymentTypes": "Failed to load payment modes. Please try again.",
    "selectAllFields": "Please select a doctor, day, and time slot.",
    "consultationAddedSuccess": "Consultation Added successfully!",
    "initiatingPayment": "Consultation Added. Initiating payment...",
    "consultationFailed": "Failed to add consultation",
    "bookConsultation": "Book a consultation",
    "onlineConsultation": "Online Consultation",
    "hospitalAppointment": "Hospital Appointment",
    "departmentSelect": "Department select",
    "selectDepartment": "Select Department",
    "myself": "myself",
    "doctorImage": "Doctor Image",
    "date": "Date",
    "time": "Time",
    "totalPayable": "Total Payable",
    "paymentSummary": "Payment Summary",
    "consultationFee": "Consultation Fee",
    "payConfirm": "Pay & Confirm",
    "chooseDoctor": "Who would you like to consult?",
    "chooseTime": "When would you like to consult?",
    "logoAlt": "Laafi Logo",
    "noDoctorsAvailable": "No doctors available for this department.",
    "noSlotsAvailable": "No available time slots for this date.",
    "errorFetchingDoctors": "Failed to load doctors. Please try again.",
    "slotsFetchFailed": "Failed to load available slots. Please try again."
  },
    "print": "Print",
  "close": "Close",
  "cancel": "Cancel"
    },
    

  },
  fr: {
    translation: {
      // Top-level strings
      dashboardMenu: 'Tableau de bord',
      login: 'Connexion',
      calendar: 'Calendrier',
      reports: 'Rapports',
      inpatient_charts: 'Admissions',
      manage_staff: 'Gérer le personnel',
      settings: 'Paramètres',
      hello_doctor: 'Bonjour Docteur',
      offline_patients: 'Patients hors ligne',
      online_patients: 'Patients en ligne',
      in_patients: 'Patients hospitalisés',
      add_article: 'Ajouter un article',
      add_schedule: 'Ajouter un horaire',
      hello: 'Bonjour',
      logout: 'Se déconnecter',
  "patient_checkout": "Sortie du Patient",
      // Header section
      header: {
        logoAlt: 'Logo Laafi',
        viewDoctor: 'Voir le Médecin',
        bookConsultation: 'Réserver une Consultation',
        loginSignup: 'Connexion/Inscription',
        login: 'Connexion',
        signup: "S'inscrire",
        toggleLanguage: 'Changer de Langue',
        english: 'Anglais',
        french: 'Français'
      },

      // About Laafi section
      aboutLaafi: {
        title: 'À propos de Laafi',
         eyebrow: 'Opérations de soins intelligentes',
  heading: 'Coordination professionnelle des soins pour chaque parcours patient.',
        description1: "Laafi est un système de gestion hospitalière tout-en-un conçu pour rationaliser et automatiser les activités hospitalières - de l'inscription du patient à sa sortie.",
        description2: 'Avec un ',
        aiPowered: 'processus de diagnostic assisté par IA',
        description3: ', Laafi améliore les soins aux patients grâce à des flux de travail intelligents et basés sur les données, permettant ainsi aux professionnels de santé de ',
        focusOnLives: 'se concentrer sur ce qui compte le plus : sauver des vies',
        description4: '.',
        keyFeatures: 'Fonctionnalités principales',
        aiDiagnosis: 'Diagnostic assisté par IA',
        aiDiagnosisDesc: 'Support de diagnostic intelligent pour les professionnels de santé',
        workflowAutomation: 'Automatisation des flux de travail',
        workflowAutomationDesc: 'Processus automatisés de l\'inscription à la sortie',
        patientCare: 'Soins aux patients améliorés',
        patientCareDesc: 'Se concentrer sur de meilleurs résultats de santé'
      },

      // What We Offer section
      whatweoffer: {
        title: 'Ce que nous offrons',
        secureAuthentication: 'Authentification sécurisée',
        bloodBank: 'Banque de sang',
        onlineConsultation: 'Consultation en ligne',
        patientManagement: 'Gestion des patients',
        labs: 'Laboratoires',
        scheduling: 'Planification',
        humanResources: 'Ressources humaines',
        aiDiagnosis: 'Diagnostic IA',
        pharmacy: 'Pharmacie',
        remoteMonitoring: 'Surveillance à distance',
        houseKeeping: 'Ménage',
        descriptions: {
          secureAuthentication: 'Protocoles de sécurité avancés pour protéger les données des patients',
          bloodBank: 'Système complet de gestion de banque de sang',
          onlineConsultation: 'Consultations virtuelles avec des professionnels de santé',
          patientManagement: 'Gestion complète des soins aux patients',
          labs: 'Système intégré de gestion de laboratoire',
          scheduling: 'Planification intelligente des rendez-vous et des ressources',
          humanResources: 'Gestion du personnel et opérations RH',
          aiDiagnosis: 'Assistance au diagnostic alimentée par l\'IA',
          pharmacy: 'Solution complète de gestion de pharmacie',
          remoteMonitoring: 'Capacités de surveillance à distance des patients',
          houseKeeping: 'Ménage et maintenance hospitalière'
        }
      },

      // Download Links section
      downloadLinks: {
        title: "Téléchargez l'application Laafi",
        description: 'Accédez à des consultations vidéo avec des médecins sur l\'application Laafi. Connectez-vous avec des médecins en ligne, disponibles 24h/24 et 7j/7, depuis le confort de votre domicile.',
        subtitle: 'Obtenez le lien pour télécharger l\'application',
        phonePlaceholder: 'Entrez le numéro de téléphone',
        sendSMS: 'Envoyer SMS',
        smsSentMessage: 'Lien de téléchargement envoyé à',
        googlePlayAlt: 'Obtenez-le sur Google Play',
        googlePlayLabel: 'Télécharger Laafi sur Google Play',
        appStoreAlt: 'Télécharger sur l\'App Store',
        appStoreLabel: 'Télécharger Laafi sur l\'App Store',
        imageAlt: 'Consultation médicale via application mobile',
        scanQR: 'Ou scannez le code QR',
        scanForAndroid: 'Scannez pour Android',
        scanForiOS: 'Scannez pour iOS',
        features: {
          videoConsultation: 'Consultation Vidéo',
          videoDescription: 'Connectez-vous avec des médecins via des appels vidéo sécurisés',
          appointments: 'Prendre Rendez-vous',
          appointmentsDescription: 'Planifiez des rendez-vous en personne ou en ligne',
          records: 'Dossiers Médicaux',
          recordsDescription: 'Accédez à vos dossiers de santé à tout moment',
          prescriptions: 'Ordonnances Numériques',
          prescriptionsDescription: 'Recevez les ordonnances sur votre téléphone'
        }
      },

      // Auth section
      auth: {
        email: 'Email',
        password: 'Mot de Passe',
        confirmPassword: 'Confirmer le Mot de Passe',
        forgotPassword: 'Mot de passe oublié ?',
        rememberMe: 'Se souvenir de moi',
        noAccount: "Vous n'avez pas de compte ?",
        hasAccount: 'Vous avez déjà un compte ?',
        signupHere: 'Inscrivez-vous ici',
        loginHere: 'Connectez-vous ici'
      },

      // Doctor section
      doctor: {
        search: 'Rechercher des Médecins',
        specialty: 'Spécialité',
        availability: 'Disponibilité',
        schedule: 'Prendre Rendez-vous',
        qualification: 'Qualification',
        experience: "Années d'Expérience",
        rating: 'Note',
        bookNow: 'Réserver Maintenant',
        viewProfile: 'Voir le Profil',
        allSpecialties: 'Toutes les Spécialités'
      },

      // Appointment section
      appointment: {
        title: 'Réserver une Consultation',
        date: 'Date',
        time: 'Heure',
        reason: 'Motif de la Visite',
        symptoms: 'Symptômes',
        book: 'Réserver le Rendez-vous',
        cancel: 'Annuler',
        type: 'Type de Rendez-vous',
        inPerson: 'En Personne',
        telemedicine: 'Télémédecine',
        emergency: 'Urgence'
      },

      // Dashboard section (nested object)
      dashboard: {
        welcome: 'Bienvenue',
        upcoming: 'Rendez-vous à Venir',
        past: 'Rendez-vous Passés',
        medicalRecords: 'Dossiers Médicaux',
        prescriptions: 'Ordonnances',
        labResults: 'Résultats de Laboratoire',
        billing: 'Facturation',
        settings: 'Paramètres',
        profile: 'Profil',
        logout: 'Déconnexion'
      },

      // Patient related
      patient: {
        consultationDetails: 'Détails de la consultation',
        mySchedules: 'Mes horaires',
        bookAppointment: 'Prendre rendez-vous',
        bookingInfo: 'Informations de réservation',
        bookingDescription: "Cliquez sur 'Prendre rendez-vous' pour programmer une nouvelle consultation.",
        pastConsultations: 'Consultations passées',
        upcomingSchedules: 'Horaires à venir'
      },

      // Table headers
      table: {
        date: 'DATE',
        doctor: 'DOCTEUR',
        patientName: 'NOM DU PATIENT',
        report: 'RAPPORT',
        status: 'STATUT',
        action: 'ACTION'
      },

      // Buttons
      button: {
        viewReport: 'Voir le rapport',
        edit: 'Modifier',
        delete: 'Supprimer',
        save: 'Enregistrer',
        cancel: 'Annuler',
        confirm: 'Confirmer',
        close: 'Fermer',
        next: 'Suivant',
        back: 'Retour',
        submit: 'Soumettre',
        reset: 'Réinitialiser'
      },

      // Messages
      message: {
        noPastConsultation: 'Aucune consultation passée trouvée',
        loading: 'Chargement...',
        noData: 'Aucune donnée disponible',
        success: 'Opération terminée avec succès',
        error: "Une erreur s'est produite",
        confirmDelete: 'Êtes-vous sûr de vouloir supprimer cet élément?',
        saveSuccess: 'Enregistré avec succès',
        updateSuccess: 'Mis à jour avec succès'
      },

      // Common terms (COMPREHENSIVE - NO DUPLICATES)
      common: {
        welcome: 'Bienvenue',
        hello: 'Bonjour',
        dashboard: 'Tableau de bord',
        profile: 'Profil',
        settings: 'Paramètres',
        logout: 'Déconnexion',
        login: 'Connexion',
        register: "S'inscrire",
        search: 'Rechercher',
        filter: 'Filtrer',
        all: 'Tous',
        pending: 'En attente',
        completed: 'Terminé',
        cancelled: 'Annulé',
        active: 'Actif',
        inactive: 'Inactif',
        notAvailable: 'N/A',
        save: 'Enregistrer',
        cancel: 'Annuler',
        update: 'Mettre à Jour',
        delete: 'Supprimer',
        addNew: 'Ajouter',
        print: 'Imprimer',
        download: 'Télécharger',
        upload: 'Téléverser',
        yes: 'Oui',
        no: 'Non',
        warning: 'Avertissement',
        info: 'Information',
        manage_patients: 'Gérer les patients',
      },

     medical: {
  consultation: 'Consultation',
  prescription: 'Ordonnance',
  diagnosis: 'Diagnostic',
  treatment: 'Traitement',
  medication: 'Médicament',
  allergy: 'Allergie',
  symptoms: 'Symptômes',
  history: 'Antécédents Médicaux',
  vitalSigns: 'Signes Vitaux',
  labResults: 'Résultats de Laboratoire',
  radiology: 'Radiologie',
  pharmacy: 'Pharmacie',
  patientDetails: 'Détails du patient',
  labTests: 'Tests de Laboratoire',
  surgerySchedule: 'Programmation chirurgicale',
  reviewComplete: 'Examen+Terminer',
  completeConsultation: 'Terminer la consultation',
  allergies: 'Allergies',
  medications: 'Médicaments',
  temperature: 'Température',
  bloodPressure: 'Tension Artérielle',
  heartRate: 'Fréquence Cardiaque',
  heightWeight: 'Taille et Poids',
  bmi: 'IMC',
  immunizations: 'Vaccinations',
  // ADD THESE MISSING KEYS:
  male: 'Masculin',
  female: 'Féminin',
  gender: 'Genre',
  dateOfBirth: 'Date de naissance',
},

      // Time/Date
      time: {
        today: "Aujourd'hui",
        yesterday: 'Hier',
        tomorrow: 'Demain',
        thisWeek: 'Cette semaine',
        thisMonth: 'Ce mois',
        nextMonth: 'Mois prochain',
        lastMonth: 'Le mois dernier',
        date: 'Date',
        time: 'Heure',
        duration: 'Durée'
      },

      // Navigation (COMPREHENSIVE - NO DUPLICATES)
      navigation: {
        home: 'Accueil',
        appointments: 'Rendez-vous',
        reports: 'Rapports',
        medications: 'Médicaments',
        billing: 'Facturation',
        help: 'Aide',
        support: 'Support',
        about: 'À Propos de Nous',
        services: 'Services',
        contact: 'Contactez-nous',
        faq: 'FAQ',
        privacy: 'Politique de Confidentialité',
        terms: 'Conditions Générales'
      },

      // Form labels
      form: {
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'Email',
        phone: 'Téléphone',
        address: 'Adresse',
        city: 'Ville',
        state: 'État',
        zipCode: 'Code postal',
        country: 'Pays',
        gender: 'Genre',
        birthDate: 'Date de naissance',
        age: 'Âge',
        bloodType: 'Groupe sanguin',
        emergencyContact: "Contact d'urgence"
      },

      // Image alt texts
      alt: {
        laafiLogo: 'Logo Laafi',
        userAvatar: 'Avatar utilisateur',
        doctorIcon: 'Icône docteur',
        patientIcon: 'Icône patient',
        appointmentIcon: 'Icône rendez-vous'
      },

      // Login component
      login: {
        signup: 'Connexion/Inscription',
        loginSignup: 'Connexion/Inscription',
        patient: 'Patient',
        doctor: 'Médecin',
        staff: 'Personnel',
        phoneEmail: 'Téléphone / Email',
        or: 'OU',
        googleLogin: 'Connexion avec Google',
        terms: "Conditions d'utilisation",
        privacy: 'Politique de confidentialité',
        welcomeBack: 'Bon retour {{name}}',
        enterMPIN: 'Veuillez saisir votre MPIN',
        forgotMPIN: 'MPIN oublié',
        loginWithOTP: 'Connexion avec OTP',
        notYou: 'Pas vous?'
      },

      // Basic details (appointment booking)
      basicDetails: {
        consultOnline: 'Consulter un médecin en ligne',
        bookHospitalAppointment: "Prendre un rendez-vous à l'hôpital",
        bookConsultation: 'Prendre un rendez-vous',
        selectDepartment: 'Sélectionner un département',
        selectDobError: 'Veuillez sélectionner votre date de naissance.'
      },

      // Doctor dashboard
     doctorDashboard: {
  myConsultations: 'Mes consultations en attente',
  myPatients: 'Mes Patients',
  mySchedules: 'Mes Programmes',
  patientServices: 'Statistics aux Patients', // Add this line
  searchPlaceholder: 'Rechercher des dossiers patients',
  logoAlt: 'Logo Laafi'
},

      // Patient details
      patientDetails: {
        name: 'Nom',
        age: 'Âge',
        gender: 'Sexe',
        mrn: 'MRN',
        notAvailable: 'N/D',
        noPatientSelected: 'Aucun patient sélectionné'
      },

      // Consultation
      consultation: {
        consultationDetails: 'Détails de la consultation',
        hpiPlaceholder: 'Commencez à taper HPI...',
        chiefComplaintPlaceholder: 'Commencez à taper les plaintes principales...',
        systemicExaminationPlaceholder: 'Commencez à taper les examens physiques...',
        finalDiagnosis: 'Diagnostic final',
        addDiagnosis: 'Ajouter un diagnostic',
        saveDiagnoses: 'Enregistrer les diagnostics',
        primaryDiagnosis: 'Diagnostic principal',
        primaryDiagnosisPlaceholder: 'Entrez le diagnostic principal...',
        notes: 'Notes',
        primaryDiagnosisNotesPlaceholder: 'Notes pour le diagnostic principal...',
        additionalDiagnosis: 'Diagnostic supplémentaire',
        diagnosis: 'Diagnostic',
        additionalDiagnosisPlaceholder: 'Entrez un diagnostic supplémentaire...',
        additionalDiagnosisNotesPlaceholder: 'Notes pour le diagnostic supplémentaire...',
        useSpecialtyTemplate: 'Utiliser un modèle de spécialité',
        aiDiagnosis: 'Diagnostic IA',
        aiAnalysis: 'Analyse IA',
        patientHistory: 'Historique du patient',
        viewPastConsultations: 'Voir les consultations passées',
        patientImages: 'Images du patient',
        uploadedImageAlt: 'Image téléchargée',
        diagnosisRegistered: 'Diagnostic enregistré avec succès !',
        diagnosisRegisterFailed: "Échec de l'enregistrement du diagnostic. Veuillez réessayer.",
        diagnosesSaved: 'Diagnostics enregistrés avec succès !',
        diagnosesSaveFailed: "Échec de l'enregistrement des diagnostics. Veuillez réessayer.",
        addDiagnosisWarning: 'Veuillez ajouter au moins un diagnostic avant d\'enregistrer.',
        addToFinalDiagnosis: 'Ajouter au diagnostic final',
        aiRecommendation: 'Recommandation IA',
        noAiRecommendation: 'Aucun texte de recommandation spécifique disponible de l\'IA.',
        aiDiagnosisFailed: 'Le diagnostic IA a échoué. Veuillez réessayer.',
        aiAnalysisFailed: "L'analyse IA a échoué. Veuillez réessayer.",
        addNewDiagnosis: 'Ajouter un nouveau diagnostic : "{{diagnosis}}"',
        diagnosisNotInSystem: "Ce diagnostic n'est pas dans le système",
        addNew: 'Ajouter'
      },

      // Medicine details
      medicineDetails: {
        prescribeMedicines: 'Prescrire des médicaments',
        noMedicinesPrescribed: 'Aucun médicament prescrit',
        prescribeMedicineButton: 'Prescrire un médicament',
        laafiRecommendation: 'Recommandation Laafi',
        laafiDescription: 'Médicaments couramment prescrits pour les symptômes donnés',
        noRecommendations: 'Aucune recommandation disponible',
        disclaimer: '*Texte de non-responsabilité'
      },

      // Lab tests
      labTest: {
        prescribeTests: 'Prescrire des tests',
        prescribeHealthPackage: 'Prescrire un forfait santé',
        prescribeLabTest: 'Prescrire un test de laboratoire',
        addPackages: 'Ajouter des forfaits',
        addTests: 'Ajouter des tests',
        noHealthPackages: 'Aucun forfait santé disponible.',
        noLabTests: 'Aucun test de laboratoire disponible.',
        healthPackages: 'Forfaits santé :',
        individualTests: 'Tests individuels :',
        noTestsPrescribed: 'Aucun test prescrit',
        prescribeHealthPackageButton: 'Prescrire un forfait santé',
        prescribeTestButton: 'Prescrire un test',
        unknownPackage: 'Forfait inconnu',
        unknownTest: 'Test inconnu'
      },

      // Review
      review: {
        reviewConsultation: 'Examen de la consultation',
        patientDetails: 'Détails du patient',
        name: 'Nom',
        age: 'Âge',
        gender: 'Genre',
        consultationDetails: 'Détails de la consultation',
        chiefComplaint: 'Plainte principale',
        finalDiagnosis: 'Diagnostic final',
        hpi: 'HPI',
        prescribedMedicines: 'Médicaments prescrits',
        noMedicinesPrescribed: 'Aucun médicament prescrit'
      },

      // Prescribe dialog
      prescribeDialog: {
        medicineInformation: 'Informations sur le médicament',
        medicineNameLabel: 'Nom du médicament',
        medicineNameHelperTextNew: 'Tapez et cliquez sur \'Ajouter à la liste principale\' dans la liste pour l\'enregistrer.',
        medicineNameHelperTextExisting: 'Sélectionnez parmi les médicaments existants ou tapez un nouveau',
        whenToTake: 'Quand prendre',
        beforeFood: 'Avant les repas',
        onceDaily: 'Une fois par jour (OID)',
        twiceDaily: 'Deux fois par jour (BID)',
        thriceDaily: 'Trois fois par jour (TID)',
        custom: 'Personnalisé',
        morning: 'Matin',
        afternoon: 'Après-midi',
        evening: 'Soir',
        night: 'Nuit',
        numberOfPills: 'Nombre de pilules',
        prescriptionPeriod: 'Période de prescription',
        startDate: 'Date de début',
        endDate: 'Date de fin',
        or: '― OU ―',
        enterFixedPills: 'Entrez un nombre fixe de pilules',
        fixedPillsHelperText: 'Entrez le nombre total de pilules si vous ne voulez pas spécifier de dates',
        calculatedTotalPills: 'Total calculé de pilules : {{fixedPills}} ({{days}} jours)',
        refills: 'Renouvellements',
        allowRefills: 'Autoriser les renouvellements ?',
        numberOfRefills: 'Nombre de renouvellements',
        totalPillsIncludingRefills: 'Total des pilules incluant les renouvellements : {{total}}',
        cancel: 'Annuler',
        savePrescription: 'Enregistrer la prescription',
        addToMasterButton: 'Ajouter à la liste principale',
        adding: 'Ajout en cours...',
        newMedicineOption: 'Ajouter "{{medicineName}}"',
        newMedicineDescription: 'Nouveau médicament - Cliquez sur **Ajouter à la liste principale**',
        newMedicine: 'Nouveau médicament',
        genericLabel: 'Générique : {{genericName}}',
        medicineAddedAlert: 'Médicament "{{medicineName}}" ajouté à la liste principale.',
        medicineAddFailedAlert: "Échec de l'ajout du nouveau médicament. Veuillez réessayer.",
        alertNoMedicineName: 'Veuillez entrer le nom du médicament.',
        alertAddToMasterFirst: 'Veuillez d\'abord ajouter le nouveau médicament à la liste principale en utilisant le bouton \'Ajouter à la liste principale\', ou sélectionnez-en un existant.',
        alertNoPillCounts: 'Veuillez spécifier quand prendre le médicament en définissant le nombre de pilules.',
        alertNoPrescriptionPeriod: 'Veuillez spécifier soit la période de prescription (Date de début et de fin), soit le nombre fixe de pilules.',
        alertMedicineIdNotFound: 'Impossible de trouver l\'ID du médicament. Veuillez fermer et réessayer ou assurez-vous que le médicament est correctement ajouté à la liste principale.',
        alertFailedToSave: "Échec de l'enregistrement de la prescription. Veuillez réessayer.",
        saving: 'Enregistrement...'
      },

      // Template selection
      templateSelection: {
        selectTemplate: 'Sélectionner un modèle',
        department: 'Département :',
        tempId: 'ID du modèle :',
        name: 'Nom :',
        image: 'Image'
      },

      // Surgery
      surgery: {
        surgeryScheduling: 'Planification de Chirurgie',
        surgeryEnabled: 'Planification de Chirurgie Activée',
        addSurgerySchedule: 'Ajouter un planning de chirurgie pour ce patient ?',
        patientInformation: 'Informations du Patient',
        mrn: 'MRN',
        name: 'Nom',
        notAvailable: 'N/D',
        surgicalProcedure: 'Procédure Chirurgicale',
        selectProcedure: 'Sélectionner une procédure',
        primarySurgeon: 'Chirurgien Principal',
        selectSurgeon: 'Sélectionner un chirurgien',
        surgeonName: 'Nom du Chirurgien',
        dr: 'Dr.',
        operatingRoom: "Salle d'Opération",
        selectOperatingRoom: "Sélectionner une salle d'opération",
        roomType: 'Type de Salle',
        category: 'Catégorie',
        scheduledDateTime: 'Date & Heure Prévue',
        durationMinutes: 'Durée (minutes)',
        anesthesiaDosage: "Dosage d'Anesthésie (mg)",
        status: 'Statut',
        notStarted: 'Non Démarré',
        scheduled: 'Planifié',
        inProgress: 'En Cours',
        completed: 'Terminé',
        cancelled: 'Annulé',
        notes: 'Notes',
        notesPlaceholder: 'Saisir des notes supplémentaires ou des instructions spéciales',
        scheduleSurgery: 'Planifier la Chirurgie',
        scheduling: 'Planification...',
        loadingMasterData: 'Chargement des données maîtres...',
        toggleInstructions: 'Activez l\'interrupteur ci-dessus pour planifier une chirurgie pour ce patient.',
        requiredFields: 'Veuillez remplir tous les champs obligatoires : Procédure Chirurgicale, Médecin, Salle, Date Prévue et Durée',
        scheduledSuccess: 'Chirurgie planifiée avec succès !',
        scheduleFailed: 'Échec de la planification de la chirurgie. Veuillez réessayer.',
        error: 'Erreur',
        roomTypes: {
          ot: 'Bloc Opératoire',
          generalWard: 'Service Général',
          nicu: 'Service de Néonatalogie',
          icu: 'Soins Intensifs',
          specialWard: 'Service Spécial',
          emergencyCare: 'Urgences',
          labour: 'Maternité',
          store: 'Réserve',
          sterileUnit: 'Unité Stérile',
          surgicalWard: 'Service Chirurgical',
          staff: 'Personnel',
          nightDuty: 'Service de Nuit'
        },
        roomCategories: {
          basic: 'Basique',
          special: 'Spécial',
          delux: 'Deluxe',
          superDelux: 'Super Deluxe',
          luxury: 'Luxe',
          vip: 'VIP',
          vvip: 'VVIP'
        }
      },

      // Past consultations
      pastConsultations: {
        consultationHistory: 'Historique des consultations',
        patientName: 'Nom du patient',
        doctorName: 'Nom du médecin',
        department: 'Département',
        date: 'Date',
        consultationType: 'Type de consultation',
        status: 'Statut',
        meetingLink: 'Lien de réunion',
        join: 'Rejoindre'
      },

      // Payment
      payment: {
        failed: 'Paiement échoué !',
        success: 'Paiement réussi !'
      },
// Add to medicineDetails section
"medicineDetails": {
  "prescribeMedicines": "Prescrire des médicaments",
  "noMedicinesPrescribed": "Aucun médicament prescrit",
  "prescribeMedicineButton": "Prescrire un médicament",
  "printPrescription": "Imprimer l'ordonnance",
  "searchMedicines": "Rechercher des médicaments prescrits...",
  "noMatchingMedicines": "Aucun médicament correspondant trouvé",
  "medicationInfo": "Info Médicament",
  "posology": "Posologie",
  "dose": "Dose",
  "frequency": "Fréquence",
  "treatmentDuration": "Durée du traitement",
  "days": "jours",
  "refills": "renouvellement(s)",
  "beforeMeal": "Avant repas",
  "afterMeal": "Après repas",
  "laafiRecommendation": "Recommandations Laafi",
  "laafiDescription": "Basé sur le diagnostic, les médicaments suivants sont recommandés :",
  "noRecommendations": "Aucune recommandation disponible",
  "disclaimer": "Veuillez examiner ces recommandations avant de prescrire"
},

// Add to prescribeDialog section
"prescribeDialog": {
  "title": "Prescrire un médicament",
  "editTitle": "Modifier le médicament",
  "medicineInformation": "Informations sur le médicament",
  "medicineNameLabel": "Nom du médicament",
  "medicineNameHelperTextExisting": "Sélectionnez un médicament existant",
  "medicineNameHelperTextNew": "Nouveau médicament - cliquez sur Ajouter à la liste",
  "newMedicine": "Nouveau médicament",
  "newMedicineOption": "Ajouter \"{{medicineName}}\"",
  "newMedicineDescription": "Ce médicament n'est pas dans la liste",
  "adding": "Ajout en cours...",
  "addToMasterButton": "Ajouter à la liste",
  "genericLabel": "Générique : {{genericName}}",
  "dosageAndForm": "Dosage et forme",
  "dosage": "Dosage",
  "dosagePlaceholder": "ex : 500mg, 10mg/5ml, 100mg/ml",
  "dosageHelperText": "Entrez le dosage/la concentration du médicament",
  "prescriptionForm": "Forme pharmaceutique",
  "selectForm": "Sélectionner une forme",
  "form": {
    "tabletScored": "Comprimé sécable",
    "tabletPlain": "Comprimé",
    "syrup": "Sirop",
    "injectable": "Injectable",
    "capsule": "Gélule",
    "inhaler": "Inhalateur",
    "drops": "Gouttes",
    "suppository": "Suppositoire",
    "sachet": "Sachet",
    "ointment": "Pommade/Crème"
  },
 "quantityPerDoseSection": "Quantité par dose",
"quantityPerDose": "Quantité par dose",
"quantityPerDosePlaceholder": "ex : 1 comprimé, 5 ml, 1 cuillère à soupe, 2 gélules",
"quantityPerDoseHelper": "Spécifiez la quantité exacte par dose (comprimés, ml, mg, etc.)",
"alertNoQuantityPerDose": "Veuillez entrer la quantité par dose",
"treatmentDuration": "Durée du traitement",
  "whenToTake": "Quand prendre le médicament",
  "beforeFood": "Avant le repas",
   "onceDaily": "1 mat",
  "twiceDaily": "1 mat, 1 apr",
  "thriceDaily": "1 mat, 1 apr, 1 soi",
  "morning": "Matin",
  "afternoon": "Après-midi",
  "evening": "Soir",
  "night": "Nuit",
  "custom": "Personnalisé",
  "numberOfPills": "Nombre de comprimés",
  "prescriptionPeriod": "la durée du traitement",
  "startDate": "Date de début",
  "endDate": "Date de fin",
  "or": "OU",
  "enterFixedPills": "Nombre de jours",
  "fixedPillsHelperText": "Nombre total de jours de traitement",
  "calculatedTotalPills": "Total : {{fixedPills}} comprimés pour {{days}} jours",
  "refills": "Renouvellements",
  "allowRefills": "Autoriser les renouvellements",
  "numberOfRefills": "Nombre de renouvellements",
  "totalPillsIncludingRefills": "Total avec renouvellements : {{total}} comprimés",
  "saving": "Enregistrement...",
  "savePrescription": "Enregistrer la prescription",
  "updatePrescription": "Mettre à jour",
  "vidalLink": "🔍 Info Médicament (Vidal)",
  "vidalLinkWithMedicine": "🔍 Vérifier {{medicine}} sur Vidal",
  "alertNoMedicineName": "Veuillez entrer un nom de médicament",
  "alertAddToMasterFirst": "Veuillez ajouter ce médicament à la liste avant de continuer",
  "alertNoDosage": "Veuillez entrer le dosage",
  "alertNoForm": "Veuillez sélectionner la forme pharmaceutique",
  "alertNoPillCounts": "Veuillez spécifier le nombre de comprimés pour au moins un moment de la journée",
  "alertNoPrescriptionPeriod": "Veuillez spécifier la période de prescription (dates ou nombre de jours)",
  "alertMedicineIdNotFound": "Erreur : Médicament introuvable. Veuillez sélectionner un médicament existant",
  "alertFailedToSave": "Erreur lors de l'enregistrement de la prescription. Veuillez réessayer",
  "medicineAddedAlert": "{{medicineName}} a été ajouté avec succès à la liste des médicaments",
  "medicineAddFailedAlert": "Erreur lors de l'ajout du médicament. Veuillez réessayer",
  "medicineSavedSuccess": "Médicament enregistré avec succès !",
  "medicineUpdatedSuccess": "Médicament mis à jour avec succès !",
  "endDateCannotBeBeforeStartDate": "La date de fin ne peut pas être antérieure à la date de début",
  "selectStartDateFirst": "Veuillez d'abord sélectionner une date de début",
  "medicineNameRequired": "Le nom du médicament est requis"
},
      // Select department (booking)
      selectDept: {
        selectAllFields: 'Veuillez sélectionner un médecin, un jour et un créneau horaire.',
        consultationAddedSuccess: 'Consultation ajoutée avec succès !',
        initiatingPayment: 'Consultation ajoutée. Initialisation du paiement...',
        consultationFailed: 'Échec de l\'ajout de la consultation',
        bookConsultation: 'Prendre un rendez-vous',
        onlineConsultation: 'Consultation en ligne',
        hospitalAppointment: 'Rendez-vous à l\'hôpital',
        departmentSelect: 'Sélection du département',
        selectDepartment: 'Sélectionner un département',
        myself: 'moi-même',
        doctorImage: 'Image du médecin',
        date: 'Date',
        time: 'Heure',
        totalPayable: 'Total à payer',
        paymentSummary: 'Résumé du paiement',
        consultationFee: 'Frais de consultation',
        payConfirm: 'Payer et confirmer',
        chooseDoctor: 'Qui souhaitez-vous consulter ?',
        chooseTime: 'Quand souhaitez-vous consulter ?',
        logoAlt: 'Logo Laafi',
        noDoctorsAvailable: 'Aucun médecin disponible pour ce service.',
        noSlotsAvailable: 'Aucun créneau horaire disponible pour cette date.',
        errorFetchingDoctors: 'Échec du chargement des médecins. Veuillez réessayer.'
      },
       "selectDept": {
    "next": "Suivant",
     "processing": "Processing...",
    "processingPayment": "Processing your payment...",
    "consultationAddedSuccess": "Consultation booked successfully!",
    "selectSlotFirst": "Veuillez d'abord sélectionner une date et une heure",
    "selectPaymentMode": "Sélectionnez le mode de paiement",
    "integrationPending": "Intégration en attente",
    "payAndConfirm": "Payer et Confirmer",
    "cardIntegrationPending": "L'intégration du paiement par carte est en attente. Veuillez sélectionner Espèces.",
    "selectPaymentMode": "Veuillez sélectionner un mode de paiement",
    "consultationAddedSuccess": "Consultation réservée avec succès !",
    "consultationFailed": "Échec de la réservation de la consultation. Veuillez réessayer.",
    "noPaymentModes": "Aucun mode de paiement disponible",
    "errorFetchingPaymentTypes": "Échec du chargement des modes de paiement. Veuillez réessayer.",
    "selectAllFields": "Veuillez sélectionner un médecin, un jour et un créneau horaire.",
    "consultationAddedSuccess": "Consultation ajoutée avec succès !",
    "initiatingPayment": "Consultation ajoutée. Initialisation du paiement...",
    "consultationFailed": "Échec de l'ajout de la consultation",
    "bookConsultation": "Prendre un rendez-vous",
    "onlineConsultation": "Consultation en ligne",
    "hospitalAppointment": "Rendez-vous à l'hôpital",
    "departmentSelect": "Sélection du département",
    "selectDepartment": "Sélectionner un département",
    "myself": "moi-même",
    "doctorImage": "Image du médecin",
    "date": "Date",
    "time": "Heure",
    "totalPayable": "Total à payer",
    "paymentSummary": "Résumé du paiement",
    "consultationFee": "Frais de consultation",
    "payConfirm": "Payer et confirmer",
    "chooseDoctor": "Qui souhaitez-vous consulter ?",
    "chooseTime": "Quand souhaitez-vous consulter ?",
    "logoAlt": "Logo Laafi",
    "noDoctorsAvailable": "Aucun médecin disponible pour ce service.",
    "noSlotsAvailable": "Aucun créneau horaire disponible pour cette date.",
    "errorFetchingDoctors": "Échec du chargement des médecins. Veuillez réessayer.",
    "slotsFetchFailed": "Échec du chargement des créneaux disponibles. Veuillez réessayer."
  }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: localStorage.getItem('preferred-language') || 'en',
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'cookie', 'navigator', 'htmlTag', 'path', 'subdomain'],
      lookupLocalStorage: 'preferred-language',
      lookupCookie: 'i18next',
      caches: ['localStorage', 'cookie'],
    },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
  });

document.documentElement.lang = i18n.language;

export default i18n;